import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'ramda';
import classnames from 'classnames';

import { Alert, Dialog, SvgIcon } from 'components/ui';
import { Divider, PrimaryButton } from '@huggg/react-components';
import { pin4 as dict } from 'config/dictionary';
import { withHuggg } from 'hoc';
import { getIsSending, getResendDelay, resend } from 'redux/modules/pin4';

import styles from './pin4Resend.module.scss';

const Pin4Resend = ({
  data: { value: message },
  isSending,
  resendDelay,
  resend,
  huggg
}) => {
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [helpContent, setHelpContent] = useState({
    title: null,
    url: null,
    styles: []
  });

  const buttonText = resendDelay
    ? dict.labels.resendLater.replace('[seconds]', resendDelay)
    : dict.labels.resend;

  const showHowItWorks = () => {
    setHelpContent({
      title: dict.labels.howItWorksTitle,
      url: 'https://www.youtube.com/embed/dgwKiYS-ZBQ',
      styles: [styles.iframeVideo],
      icon: 'video'
    });
    setShowHelpDialog(true);
  };

  const showMap = () => {
    setHelpContent({
      title: dict.labels.atmLocatorTitle,
      url: 'https://www.pin4cashatm.com/en/map/',
      styles: [styles.iframeMap],
      icon: 'location'
    });
    setShowHelpDialog(true);
  };

  return (
    <div data-test-id="voucher-pin4-resend">
      <div className={styles.callout}>
        <div className={styles.calloutInner}>
          <Alert type="success">{message}</Alert>
          <div className={styles.toggle}>
            <div
              className={styles.toggleHandle}
              onClick={() => setShowResend(!showResend)}
            >
              {dict.labels.didntReceiveSms}
              <SvgIcon
                name="carat-left"
                className={classnames(
                  styles.toggleArrow,
                  showResend ? styles.toggleArrowOpen : undefined
                )}
              />
            </div>
          </div>
          {showResend && (
            <div className={styles.toggleContent}>
              <PrimaryButton
                disabled={Boolean(isSending || resendDelay)}
                onClick={() => resend(huggg.id)}
                loading={isSending}
                label={buttonText}
              />
              <p className={styles.helpText}>{dict.labels.wrongNumberHelp}</p>
            </div>
          )}
        </div>
      </div>

      <Divider />

      <div className={styles.nextSteps}>
        <h2 className={styles.nextStepsTitle}>{dict.labels.nextSteps}</h2>

        <div className={styles.moreInfoButtons}>
          {/* Not using our Button component here as these are weird, one-off pseudo-buttons */}
          {/* Just using the <button> component for accessibility. */}
          <button className={styles.moreInfoButton} onClick={showHowItWorks}>
            <SvgIcon name="video" />
            {dict.labels.seeHowItWorks}
          </button>
          <button className={styles.moreInfoButton} onClick={showMap}>
            <SvgIcon name="location" />
            {dict.labels.findAtm}
          </button>
        </div>
      </div>

      <Dialog
        disableOverlayClick={false}
        visible={showHelpDialog}
        contentClassName={styles.dialogContent}
        showCloseButton={true}
        title={
          <>
            {helpContent.icon && (
              <SvgIcon name={helpContent.icon} className={styles.titleIcon} />
            )}
            {helpContent.title}
          </>
        }
        onClose={() => setShowHelpDialog(false)}
      >
        <div
          className={classnames(styles.iframeWrapper, ...helpContent.styles)}
        >
          <iframe
            src={helpContent.url}
            title={helpContent.title}
            className={styles.iframe}
          />
        </div>
      </Dialog>
    </div>
  );
};

Pin4Resend.propTypes = {
  data: PropTypes.object.isRequired,
  resend: PropTypes.func.isRequired,
  huggg: PropTypes.object.isRequired,
  isSending: PropTypes.bool,
  resendDelay: PropTypes.number
};

const mapStateToProps = state => ({
  isSending: getIsSending(state),
  resendDelay: getResendDelay(state)
});

export default compose(
  withHuggg,
  connect(mapStateToProps, { resend })
)(Pin4Resend);
